import { useEffect, useRef, useState, type ComponentType } from "react";
import { useSearchParams } from "react-router-dom";
import {
  IconBuildingSkyscraper,
  IconFileDescription,
  IconBell,
  IconShield,
  IconCreditCard,
  IconHistory,
  IconLogout,
  IconPencil,
  IconTruckDelivery,
  IconFileInvoice,
} from "@tabler/icons-react";
import { clsx } from "clsx";
import { Heading, Text } from "../../../../components/common/typography";
import { Button } from "../../../../components/common/button";
import { useMe } from "../../../../swr";
import { useLogout } from "../../../authentication/hooks/useLogout";
import {
  CompanyProfileView,
  CompanyProfileEdit,
  CompanyProfileCompletionBanner,
} from "../components/company-profile-section";
import { CompanyLogoCard } from "../components/company-logo-card";
import { ShippingAddressesSection } from "../components/shipping-addresses-section";
import { BillingAddressesSection } from "../components/billing-addresses-section";
import { DocumentsSection } from "../components/documents-section";
import { NotificationsSection } from "../components/notifications-section";
import { SecuritySection } from "../components/security-section";
import { BillingSection } from "../components/billing-section";
import { ChangeRequestsSection } from "../../../change-requests/presentation/components/change-requests-section";
import { useMyPendingProfileChangeRequest } from "../../../change-requests/hooks/useMyPendingProfileChangeRequest";

type Section =
  | "company-profile"
  | "shipping-addresses"
  | "billing-addresses"
  | "documents"
  | "notifications"
  | "security"
  | "payments"
  | "change-requests";

const NAV_ITEMS: { id: Section; label: string; icon: ComponentType<{ size?: number; stroke?: number; className?: string }> }[] = [
  { id: "company-profile",     label: "Company profile",   icon: IconBuildingSkyscraper },
  { id: "shipping-addresses",   label: "Shipping addresses", icon: IconTruckDelivery },
  { id: "billing-addresses",    label: "Billing addresses",  icon: IconFileInvoice },
  { id: "documents",           label: "Documents",          icon: IconFileDescription },
  { id: "notifications",        label: "Notifications",      icon: IconBell },
  { id: "security",             label: "Security",           icon: IconShield },
  { id: "payments",             label: "Payments",           icon: IconCreditCard },
  { id: "change-requests",      label: "Change requests",    icon: IconHistory },
];

const DEFAULT_SECTION: Section = "company-profile";
const VALID_SECTIONS = new Set<Section>(NAV_ITEMS.map((n) => n.id));

function getInitials(name?: string | null): string {
  if (!name) return "AC";
  return name.split(" ").filter(Boolean).slice(0, 2).map((w) => w[0].toUpperCase()).join("");
}

function isProfileComplete(
  user?: {
    companyName?: string | null;
    crNumber?: string | null;
    taxNumber?: string | null;
    street?: string | null;
    street2?: string | null;
    city?: string | null;
    zip?: string | null;
    state?: { id?: number | null; name?: string | null } | null;
    country?: { id?: number | null; name?: string | null } | null;
    contactName?: string | null;
    name?: string | null;
  } | null,
): boolean {
  return !!(
    user?.companyName &&
    user?.crNumber &&
    user?.taxNumber &&
    user?.street &&
    user?.city &&
    user?.zip &&
    user?.country?.name &&
    (user?.contactName || user?.name)
  );
}

export function AccountPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get("tab") as Section | null;
  const activeSection: Section =
    tabParam && VALID_SECTIONS.has(tabParam) ? tabParam : DEFAULT_SECTION;
  const setActiveSection = (id: Section) => {
    const next = new URLSearchParams(searchParams);
    if (id === DEFAULT_SECTION) next.delete("tab");
    else next.set("tab", id);
    setSearchParams(next, { replace: false });
  };
  const [isEditing, setIsEditing] = useState(false);
  const { user } = useMe();
  const { data: pendingProfile } = useMyPendingProfileChangeRequest();
  const profileComplete = isProfileComplete(user);
  const profilePending = !!pendingProfile;
  useEffect(() => {
    if (profilePending && isEditing) setIsEditing(false);
  }, [profilePending, isEditing]);

  const initials    = getInitials(user?.name);
  const displayName = user?.name  ?? "Acme Corporation";
  const email       = user?.email ?? "procurement@acme.qa";

  const handleLogout = useLogout();

  // Phones: the section nav is a horizontal strip — keep the active tab in view.
  const navStripRef = useRef<HTMLElement>(null);
  useEffect(() => {
    const strip = navStripRef.current;
    const active = strip?.querySelector<HTMLElement>('[aria-current="page"]');
    if (!strip || !active || strip.scrollWidth <= strip.clientWidth) return;
    const stripRect = strip.getBoundingClientRect();
    const activeRect = active.getBoundingClientRect();
    strip.scrollLeft += activeRect.left - stripRect.left - (stripRect.width - activeRect.width) / 2;
  }, [activeSection]);

  return (
    <div className="flex flex-col gap-6 px-8 py-6 max-lg:gap-4 max-lg:px-4 max-lg:py-4">
      <div className="flex items-center justify-between gap-3">
        <Heading variant="h2" tone="dark">Account</Heading>
        {activeSection === "company-profile" && !isEditing && !profilePending && (
          <Button
            variant="secondary"
            size="sm"
            leftIcon={<IconPencil size={16} />}
            className="!bg-bgWhite"
            onClick={() => setIsEditing(true)}
          >
            Edit
          </Button>
        )}
      </div>

      <div className="flex items-start gap-6 max-lg:flex-col max-lg:items-stretch max-lg:gap-4">
        {/* Phones: the section nav turns into a horizontally scrollable strip above the content. */}
        <aside ref={navStripRef} className="w-56 shrink-0 rounded-2xl border border-borderDefault bg-bgLight px-3 py-4 min-h-[480px] max-lg:flex max-lg:w-full max-lg:min-h-0 max-lg:items-center max-lg:gap-0.5 max-lg:overflow-x-auto max-lg:p-1.5">
          <div className="flex items-center gap-3 px-2 py-2 max-lg:hidden">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-textDark">
              <Text variant="bodySmBold" tone="inherit" className="text-bgWhite leading-none">{initials}</Text>
            </div>
            <div className="min-w-0">
              <Text variant="bodyBold" tone="dark" className="truncate">{displayName}</Text>
              <Text variant="bodyXs" tone="muted" className="truncate">{email}</Text>
            </div>
          </div>

          <div className="mt-1 mb-1 border-t border-borderDefault max-lg:hidden" />

          <nav className="flex flex-col gap-0.5 max-lg:shrink-0 max-lg:flex-row">
            {NAV_ITEMS.map(({ id, label, icon: Icon }) => (
              <button
                key={id}
                type="button"
                onClick={() => setActiveSection(id)}
                aria-current={activeSection === id ? "page" : undefined}
                className={clsx(
                  "flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left transition-colors max-lg:w-auto max-lg:shrink-0 max-lg:gap-2 max-lg:whitespace-nowrap",
                  activeSection === id
                    ? "bg-bgWhite shadow-md border border-borderDefault text-textDark"
                    : "text-textBody hover:bg-bgWhite hover:text-textDark",
                )}
              >
                <Icon size={18} className="shrink-0" />
                <Text variant="body" tone="inherit">{label}</Text>
              </button>
            ))}
          </nav>

          <div className="mt-3 max-lg:mt-0 max-lg:shrink-0">
            <button
              type="button"
              className="flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left text-accentError transition-colors hover:bg-accentErrorTint max-lg:w-auto max-lg:gap-2 max-lg:whitespace-nowrap"
              onClick={handleLogout}
            >
              <IconLogout size={18} className="shrink-0" />
              <Text variant="body" tone="inherit">Sign out</Text>
            </button>
          </div>
        </aside>

        <main className="flex flex-1 flex-col gap-4 max-lg:w-full max-lg:min-w-0">
          {activeSection === "company-profile" && (
            <div className="flex flex-col gap-4">
              {/* Hidden while editing: the form it points to is already open. */}
              {!profileComplete && !profilePending && !isEditing && (
                <CompanyProfileCompletionBanner onEdit={() => setIsEditing(true)} />
              )}
              {isEditing ? (
                <CompanyProfileEdit
                  onCancel={() => setIsEditing(false)}
                  onSubmitted={() => setIsEditing(false)}
                />
              ) : (
                <>
                  <CompanyLogoCard />
                  <CompanyProfileView />
                </>
              )}
            </div>
          )}
          {activeSection === "shipping-addresses" && <ShippingAddressesSection />}
          {activeSection === "billing-addresses" && <BillingAddressesSection />}
          {activeSection === "documents" && <DocumentsSection />}
          {activeSection === "notifications" && <NotificationsSection />}
          {activeSection === "security" && <SecuritySection />}
          {activeSection === "payments" && <BillingSection />}
          {activeSection === "change-requests" && <ChangeRequestsSection />}
        </main>
      </div>
    </div>
  );
}

export default AccountPage;
